/**
 * CORE-M-01 Management category bar — single source of truth for the seven
 * tabs, their order, their URL slugs and their operator-facing labels.
 *
 * Order mirrors Chans's 2026-09-15 spec §2.1: the left-to-right sequence of
 * an actual PPC review. Every label uses Google Ads vocabulary (G-1);
 * invented product names like "Easy Yes / No" or "Needs a look" belong
 * to StrategyBoard, not here (G-9). The absence test in
 * ManagementCategoryBar.test.tsx locks this.
 *
 * As CORE-M-02 through CORE-M-08 ship, each tab's `Coming in ...` placeholder
 * body is replaced by the real content. Do not rename slugs after they ship
 * in production — they land in operators' bookmarks.
 */
export const MANAGEMENT_TAB_SLUGS = [
  'google-recommendations',
  'negatives',
  'campaign-review',
  'ad-group-review',
  'ad-review',
  'keyword-review',
  'budget-reallocation',
] as const;

export type ManagementTabSlug = (typeof MANAGEMENT_TAB_SLUGS)[number];

export interface ManagementTabDef {
  slug: ManagementTabSlug;
  label: string;
  ticket: string;
}

export const MANAGEMENT_TABS: readonly ManagementTabDef[] = [
  { slug: 'google-recommendations', label: 'Google Recommendations', ticket: 'CORE-M-02' },
  { slug: 'negatives', label: 'Negative Keyword Suggestions', ticket: 'CORE-M-03' },
  { slug: 'campaign-review', label: 'Campaign Review', ticket: 'CORE-M-04' },
  { slug: 'ad-group-review', label: 'Ad Group Review', ticket: 'CORE-M-05' },
  { slug: 'ad-review', label: 'Ad Review', ticket: 'CORE-M-06' },
  { slug: 'keyword-review', label: 'Keyword Review', ticket: 'CORE-M-07' },
  { slug: 'budget-reallocation', label: 'Budget Reallocation', ticket: 'CORE-M-08' },
] as const;

export const DEFAULT_MANAGEMENT_TAB: ManagementTabSlug = 'google-recommendations';

export function isManagementTabSlug(v: string | null | undefined): v is ManagementTabSlug {
  return !!v && (MANAGEMENT_TAB_SLUGS as readonly string[]).includes(v);
}
